import { PageData } from '../types/route-types';
import { SuggestedPage } from './file-validation.service';
import { LightHttp } from './light-http.service';
import { ApiConfig } from './api-config';

export class FeatureRedirectService {
    private lightHttp: LightHttp;
    private apiConfig: ApiConfig;

    constructor(lightHttp: LightHttp, apiConfig: ApiConfig) {
        this.lightHttp = lightHttp;
        this.apiConfig = apiConfig;
    }

    private static readonly EXT_TO_MEDIA: Record<string, string> = {
        // image
        jpg: 'image', jpeg: 'image', jfif: 'image', png: 'image', gif: 'image', webp: 'image',
        bmp: 'image', tiff: 'image', tif: 'image', svg: 'image', ico: 'image', heic: 'image',
        heif: 'image', avif: 'image', psd: 'image', eps: 'image', ppm: 'image',
        // video
        mp4: 'video', mov: 'video', avi: 'video', mkv: 'video', webm: 'video', wmv: 'video',
        flv: 'video', mpg: 'video', mpeg: 'video', '3gp': 'video', m4v: 'video', ts: 'video',
        // audio
        mp3: 'audio', wav: 'audio', ogg: 'audio', flac: 'audio', aac: 'audio', m4a: 'audio',
        wma: 'audio', opus: 'audio', ac3: 'audio', aiff: 'audio',
        // document
        pdf: 'document', doc: 'document', docx: 'document', pptx: 'document',
        // archive
        zip: 'archive', rar: 'archive', '7z': 'archive', tar: 'archive',
    };

    async findRelevantPages(
        fileExtension: string,
        currentPageData: PageData,
        limit: number = 8,
    ): Promise<SuggestedPage[]> {
        const currentAction = currentPageData.processType || '';
        const currentToExt = (currentPageData.toType || '').toString().toLowerCase();
        const mediaType = FeatureRedirectService.EXT_TO_MEDIA[fileExtension] || '';

        // Single query with all relevant terms — Lucene SHOULD matching will score by term overlap
        const queryTerms = [fileExtension, mediaType, currentToExt, currentAction].filter(Boolean);
        const query = queryTerms.join(' ');

        const currentSlug = (currentPageData.path || '').replace(/^\//, '').toLowerCase();

        return new Promise<SuggestedPage[]>((resolve) => {
            this.lightHttp.get<{ results: FeatureSearchResult[] }>(
                this.apiConfig.getFeatureSearchEndpoint(query),
            ).subscribe({
                next: (response) => {
                    const results = response?.results || [];
                    resolve(this.rankResults(results, fileExtension, currentAction?.toString(), currentToExt, currentSlug));
                },
                error: () => resolve([]),
            });
        });
    }

    /**
     * Rank recovery destinations for a file this page cannot take.
     *
     * The signal that matters is **what the user was trying to do**, not what this page happens
     * to produce. Someone who dropped a PNG on /compress-gif chose *compress*; the *gif* part is
     * the thing they got wrong. The file is ground truth, the action is intent, and the page's
     * output format is the mistake — so it must not drive the ranking.
     *
     * It used to. `fromFormat === file && toFormat === currentTarget` scored +300 on top of
     * +150 for matching the target, which put "Convert PNG To GIF" at 530 and "Compress PNG" at
     * 180. So a user with two photos on the GIF compressor was told, first and loudest, to turn
     * their photos into GIFs — a lossy downgrade nobody asks for, and not remotely what they
     * came to do.
     *
     * Order now: same action on my exact format, then same action on my media type (the answer
     * for a mixed batch), then conversions out of my format, and conversions *into* this page's
     * format last — actively penalised when that would be a downgrade.
     */
    private rankResults(
        results: FeatureSearchResult[],
        fileExtension: string,
        currentAction: string,
        currentToExt: string,
        currentSlug = '',
    ): SuggestedPage[] {
        const action = (currentAction || '').toLowerCase();
        const media = FeatureRedirectService.EXT_TO_MEDIA[fileExtension] || '';
        // The uploaded file is ALREADY the format this page produces (e.g. an .mp3 dropped on
        // /convert-wma-to-mp3 — 82 rejections in 14 days). Converting it to the format it
        // already is is a no-op the user can see is pointless.
        const alreadyTargetFormat = !!currentToExt && fileExtension === currentToExt;

        return results
            // Never suggest the page they are already on. "These files can't be used with
            // Compress GIF -- here's where they belong: Compress GIF" is what happens without
            // this, and it reads as the site being broken.
            .filter(r => (r.slug || '').toLowerCase() !== currentSlug)
            // A page that names a specific input format has to name THEIRS. Without this,
            // "Compress AIF" and "Compress TIF" were offered for a PNG purely for sharing the
            // word "compress" -- destinations that would reject the file all over again.
            .filter(r => {
                const { fromFormat } = this.parseSlugFormats(r.slug);
                return fromFormat === null || fromFormat === fileExtension || fromFormat === media;
            })
            .map(r => {
                let score = 0;
                const { fromFormat, toFormat } = this.parseSlugFormats(r.slug);
                const sameAction = !!action && r.category === action;
                const genericForMedia = fromFormat === null || fromFormat === media;

                // 1. Do what they asked, to the file they brought.
                if (sameAction && fromFormat === fileExtension) score += 500;
                // 2. Do what they asked, to this kind of file. This is what a mixed batch needs:
                //    one destination that takes the png and the jpg.
                if (sameAction && genericForMedia) score += 400;
                // 3. Any tool that takes this exact format is at least usable.
                if (fromFormat === fileExtension) score += 120;
                // 4. Same action anywhere else.
                if (sameAction) score += 80;

                // 5. Converting INTO this page's target is last, and only when it is not a
                //    downgrade. Photos to GIF loses colour and gains size; offering it above a
                //    compressor is how this went wrong in the first place.
                if (toFormat && toFormat === currentToExt) {
                    const downgrade = media === 'image' && currentToExt === 'gif' && fileExtension !== 'gif';
                    score += downgrade ? -150 : 40;
                }

                // 6. The file is already the format this page produces -- an .mp3 dropped on
                //    /convert-wma-to-mp3, 82 rejections in 14 days. Every conversion into that
                //    format is a visible no-op, and the measured answer for this population is
                //    that they want the file smaller. This outranks the action match on
                //    purpose: their action cannot be satisfied by a page that would hand them
                //    back what they already have.
                if (alreadyTargetFormat) {
                    if (r.category === 'compress' && fromFormat === fileExtension) score += 600;
                    if (toFormat === currentToExt) score -= 300;
                }

                return { url: r.url, title: r.title, category: r.category, score };
            })
            .filter(r => r.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 8)
            .map(({ score, ...rest }) => rest);
    }

    private parseSlugFormats(slug: string): { fromFormat: string | null, toFormat: string | null } {
        // convert-jpg-to-gif → from=jpg, to=gif
        const twoExt = slug.match(/^(?:convert|merge)-([a-z0-9.]+)-to-([a-z0-9.]+)$/);
        if (twoExt) return { fromFormat: twoExt[1], toFormat: twoExt[2] };
        // compress-jpg → from=jpg, to=null
        const singleExt = slug.match(/^(?:compress|crop|resize|rotate|flip|trim|split|extract|cut)-([a-z0-9.]+)$/);
        if (singleExt) return { fromFormat: singleExt[1], toFormat: null };
        return { fromFormat: null, toFormat: null };
    }
}

interface FeatureSearchResult {
    slug: string;
    url: string;
    title: string;
    category: string;
    fromFormat?: string;
    toFormat?: string;
}
